import React, { useEffect } from 'react'

import { useCriteriaManager } from '../hooks/useCriteriaManager'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'

import { AddIcon } from '@chakra-ui/icons'
import { Box, Button, Text } from '@chakra-ui/react'

import { useAiOperatorFieldsQuery } from '@/graphql/generated/hooks'
import {
  AiOperatorActionType,
  AiOperatorFieldType,
  AiOperatorResponseRule,
} from '@/graphql/generated/schemas'

import { Criterion } from '../types/types'
import { modifyAiOperatorCriteriaFields } from '../utils/utils'
import { AiOperatorCriteriaLoader } from './AiOperatorCriteriaLoader'
import { AiOperatorCriterionItem } from './AiOperatorCriterionItem'
import { AiOperatorNoCriteriaBanner } from './AiOperatorNoCriteriaBanner'

interface AiOperatorCriteriaIProps {
  onChange?: (criteria: Criterion[]) => void
  disabled?: boolean
  previewData?: AiOperatorResponseRule[]
}

const AiOperatorCriteria: React.FC<AiOperatorCriteriaIProps> = ({
  onChange,
  disabled = false,
  previewData,
}) => {
  const shouldSkipQuery = disabled && !!previewData?.length

  const { data, loading: isLoading } = useAiOperatorFieldsQuery({
    variables: {
      filter: {
        type: AiOperatorFieldType.BooleanLabel,
      },
    },
    fetchPolicy: 'cache-and-network',
    skip: shouldSkipQuery,
  })

  const {
    criteria,
    availableCriteria,
    hasError: hasNoCriterion,
    handleAddCriterion,
    handleDelete,
    handleDragEnd,
    initializeCriteria,
    handleUpdateCriterion,
  } = useCriteriaManager(onChange)

  useEffect(() => {
    if (data && !isLoading && !disabled) {
      const backendCriteria = modifyAiOperatorCriteriaFields(data)

      initializeCriteria(backendCriteria)
    }
  }, [data, isLoading])

  useEffect(() => {
    if (disabled && previewData?.length > 0) {
      const sortedPreviewData = [...previewData].sort(
        (a, b) => (b.priority ?? 0) - (a.priority ?? 0)
      )

      const previewCriteria = sortedPreviewData.map((rule) => ({
        id: rule.field?.id,
        action: AiOperatorActionType.ResolveAndMarkAsFalse,
        criterion: {
          value: rule.field?.id,
          label: rule.field?.description,
        },
        detected: true,
      }))

      initializeCriteria(previewCriteria)
    }
  }, [disabled, previewData])

  if (isLoading && !disabled) {
    return <AiOperatorCriteriaLoader />
  }

  return (
    <Box mb={6} width='100%'>
      <Text fontSize='16px' fontWeight='medium' letterSpacing='-0.8px' mb={2}>
        AI Operator Criteria
      </Text>
      <Text color='gray.600' fontSize='sm' mb={4}>
        Drag and drop to reorder the criteria. The top item will be checked
        first.
      </Text>

      {hasNoCriterion && (
        <AiOperatorNoCriteriaBanner
          hasNoCriterion={hasNoCriterion}
          message='There are no criteria for this rule'
        />
      )}

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId='criteria-list'>
          {(provided) => (
            <Box
              {...provided.droppableProps}
              mb={4}
              ref={provided.innerRef}
              width='100%'
            >
              {criteria.map((item, index) => (
                <Draggable draggableId={item.id} index={index} key={item.id}>
                  {(provided) => (
                    <AiOperatorCriterionItem
                      availableCriteria={availableCriteria}
                      disabled={disabled}
                      handleDelete={handleDelete}
                      handleUpdateCriterion={handleUpdateCriterion}
                      item={item}
                      provided={provided}
                    />
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </Box>
          )}
        </Droppable>
      </DragDropContext>

      {!disabled && (
        <Button
          borderRadius='md'
          isDisabled={availableCriteria.length === 0}
          leftIcon={<AddIcon />}
          onClick={handleAddCriterion}
          size='sm'
          variant='outline'
        >
          Add criteria{' '}
        </Button>
      )}
    </Box>
  )
}

export { AiOperatorCriteria }
